import React, { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { Maximize2, X } from 'lucide-react'
import { useMiniPlayer } from '../contexts/MiniPlayerContext'
import { cn } from '../lib/utils'

// Floating mini player. Renders the YouTube iframe (or v.redd.it
// <video>) in a fixed corner so the user can keep watching while
// navigating other parts of the app. Lives at App level so route
// changes don't unmount it.
//
// Position: bottom-right (start side in RTL), above the bottom tab
// bar with a small inset. Draggable not implemented this round; if
// it gets in the way of content we can revisit.
//
// Tap the body → expand back to inline / route to the article page.
// Tap the X → close + unmount the iframe.

export default function MiniPlayer() {
  const { t } = useTranslation()
  const { video, closeMini } = useMiniPlayer()
  const videoRef = useRef(null)
  const audioRef = useRef(null)
  // Sync the video + audio elements for Reddit-mp4 mode (same
  // strategy as RedditMedia.VideoBlock — Reddit serves audio as a
  // separate DASH stream).
  useEffect(() => {
    if (!video || video.kind !== 'reddit-mp4' || !video.audioSrc) return undefined
    const v = videoRef.current
    const a = audioRef.current
    if (!v || !a) return undefined
    const onPlay = () => { a.currentTime = v.currentTime; a.play().catch(() => {}) }
    const onPause = () => { a.pause() }
    const onSeeked = () => { a.currentTime = v.currentTime }
    const onRate = () => { a.playbackRate = v.playbackRate }
    v.addEventListener('play', onPlay)
    v.addEventListener('pause', onPause)
    v.addEventListener('seeked', onSeeked)
    v.addEventListener('ratechange', onRate)
    return () => {
      v.removeEventListener('play', onPlay)
      v.removeEventListener('pause', onPause)
      v.removeEventListener('seeked', onSeeked)
      v.removeEventListener('ratechange', onRate)
    }
  }, [video])

  const [expanded, setExpanded] = useState(false)
  useEffect(() => { if (!video) setExpanded(false) }, [video])

  if (!video) return null
  if (typeof document === 'undefined') return null

  // Reserve room above the bottom tab bar (72px + safe area inset).
  // start-3 in RTL puts the player on the start edge (right in AR).
  // Expanded variant takes ~80% of the viewport width; collapsed
  // sits at 220px — comfortable on phones, not blocking content.
  return createPortal(
    <div
      className={cn(
        'fixed z-40 transition-all duration-200',
        // Above the tab bar; safe area covers iPhone home indicator.
        'end-3',
        expanded ? 'inset-x-3' : '',
      )}
      style={{
        bottom: 'calc(72px + env(safe-area-inset-bottom) + 0.75rem)',
      }}
    >
      <div
        className={cn(
          'relative rounded-xl overflow-hidden bg-black shadow-2xl border border-white/10',
          expanded ? 'aspect-video w-full' : 'w-[220px] aspect-video ms-auto',
        )}
      >
        {video.kind === 'youtube' && (
          <iframe
            title={video.title || 'YouTube mini player'}
            className="absolute inset-0 w-full h-full"
            src={`https://www.youtube-nocookie.com/embed/${video.src}?autoplay=1&playsinline=1`}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
            referrerPolicy="strict-origin-when-cross-origin"
          />
        )}
        {video.kind === 'reddit-mp4' && (
          <>
            <video
              ref={videoRef}
              src={video.src}
              autoPlay
              controls
              playsInline
              muted={!video.audioSrc}
              className="absolute inset-0 w-full h-full object-contain bg-black"
            />
            {video.audioSrc && (
              <audio
                ref={audioRef}
                src={video.audioSrc}
                preload="auto"
                style={{ display: 'none' }}
              />
            )}
          </>
        )}

        {/* Controls overlay — kept off the iframe's hit area in YouTube
            mode so the user can still tap the player to interact.
            X closes; the expand icon toggles between mini + ~viewport
            size, scope-creeping back into inline playback is a
            follow-up. */}
        <div className="absolute top-1 end-1 flex gap-1 z-10">
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            aria-label={t('miniPlayer.expand')}
            className="w-7 h-7 rounded-full bg-black/70 text-white inline-flex items-center justify-center hover:bg-black/85"
          >
            <Maximize2 className="w-3.5 h-3.5" />
          </button>
          <button
            type="button"
            onClick={closeMini}
            aria-label={t('miniPlayer.close')}
            className="w-7 h-7 rounded-full bg-black/70 text-white inline-flex items-center justify-center hover:bg-red-600/90"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </div>,
    document.body,
  )
}
